/**
 * $store.bundle — product-page pack-size + flavor-slot state (IIFE, product templates only)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * The single writer of `$store.selection` on Rooty product pages. Quantity
 * cards and flavor slots write here; the gifts widget, the Appstle gate, the
 * shipping badge and the price header read from here. Exactly one derivation
 * of "what Add to Cart adds".
 *
 * State
 *   slots      flavor variant ids, one per pack. `quantity` (the pack size) IS
 *              `slots.length`: 1 = the Regular product (subscription-eligible),
 *              N = the bundle variant whose `Quantity` option is N.
 *
 * Derived
 *   bundleVariant   the bundle variant matching `quantity` (null for 1x)
 *   isBundle        !!bundleVariant
 *   mainVariant     the Regular variant in slot 0 (drives ?variant=, ingredients, Appstle)
 *   gifts           the bundle variant's `custom.bundle_gifts` ({ productId, variantId })
 *   properties      { _bundleData, _gifts } line properties the Cart Transform
 *                   expand function consumes — null for 1x
 *
 * Wire contract (what the bundle line carries to /cart/add):
 *   _bundleData  '{"items":[{"id":"gid://shopify/ProductVariant/<id>","quantity":n},…]}'
 *                flavors aggregated by variant, then gifts appended as ordinary items —
 *                the bundle price is fixed on the bundle variant, so gifts are free by
 *                construction
 *   _gifts       comma-joined gift variant ids; the cart drawer uses it to render those
 *                components in the FREE style (presentation only — see cart-item-components)
 *
 * Loaded `defer` from layout/theme.liquid on `template.name == 'product'` only,
 * after alpine-stores.js and before Alpine core, so `alpine:init` sees it.
 * Reads window.Theme.pageData.product once (Tier 1 — frozen).
 */
(() => {
  'use strict';

  const VARIANT_GID = 'gid://shopify/ProductVariant/';

  window.Theme = window.Theme || {};
  const Logger = window.Theme.Logger || {
    ns: () => ({ debug: () => {}, info: () => {}, warn: console.warn.bind(console), error: console.error.bind(console) }),
  };
  const log = Logger.ns('store.bundle');

  /**
   * Aggregate flavor slots into `_bundleData` items: one entry per distinct
   * variant, quantity = number of slots holding it. Slot order is preserved by
   * first appearance.
   * @param {number[]} slots - flavor variant ids, one per pack
   * @returns {{ id: string, quantity: number }[]}
   */
  const aggregateSlots = (slots) => {
    const byId = new Map();
    for (const id of slots) {
      if (!id) continue;
      byId.set(id, (byId.get(id) || 0) + 1);
    }
    return Array.from(byId, ([id, quantity]) => ({ id: VARIANT_GID + id, quantity }));
  };

  /**
   * Build the line properties for a bundle add. Pure — exercised directly by
   * tests/e2e/bundle-flavor-slots.spec.ts without any cart traffic.
   * @param {number[]} slots - flavor variant ids
   * @param {{ variantId: number }[]} gifts - the bundle variant's gifts
   * @returns {{ _bundleData: string, _gifts?: string }}
   */
  const buildBundleProperties = (slots, gifts = []) => {
    const items = aggregateSlots(slots).concat(
      gifts.map((g) => ({ id: VARIANT_GID + g.variantId, quantity: 1 }))
    );
    const props = { _bundleData: JSON.stringify({ items }) };
    if (gifts.length) props._gifts = gifts.map((g) => String(g.variantId)).join(',');
    return props;
  };

  /**
   * Per-serving price and save% for a priced selection. Shared with
   * appstle-selection-bridge.js so a pack-size click and a plan change derive
   * the header's numbers identically. FLOORS to mirror the SSR Liquid in
   * cart-card-header.liquid (`price | divided_by: days` is integer division) —
   * no off-by-one flicker when Alpine overwrites the baked value.
   * @param {number} priceCents
   * @param {number|null} compareCents
   * @param {number} totalDays - servings the customer gets (packs × servingsPerPack)
   * @returns {{ perServingCents: number, savePct: number }}
   */
  const recalcDerived = (priceCents, compareCents, totalDays) => {
    const days = totalDays > 0 ? totalDays : 30;
    const perServingCents = Math.floor(priceCents / days);
    const savePct = compareCents && compareCents > priceCents
      ? Math.floor(((compareCents - priceCents) * 100) / compareCents)
      : 0;
    return { perServingCents, savePct };
  };

  window.Theme.buildBundleProperties = buildBundleProperties;
  window.Theme.recalcDerived = recalcDerived;

  const fmt = (cents) =>
    typeof window.Theme.formatMoney === 'function' ? window.Theme.formatMoney(cents) : '';

  document.addEventListener('alpine:init', () => {
    const p = window.Theme.pageData && window.Theme.pageData.product;
    if (!p) return;

    // Bundle variants sorted by pack size (page-data emits admin order).
    const bundleVariants = ((p.bundle && p.bundle.variants) || [])
      .slice()
      .sort((a, b) => a.quantity - b.quantity);

    Alpine.store('bundle', {
      productId: p.productId,
      variants: p.variants || [],
      bundle: p.bundle ? { ...p.bundle, variants: bundleVariants } : null,
      servingsPerPack: p.servingsPerPack || 30,

      slots: [p.currentVariantId],

      // ─── Derived ────────────────────────────────────────────────────────

      /** Pack size = one slot per pack. */
      get quantity() {
        return this.slots.length;
      },

      /** Pack sizes offered: [1, ...bundle quantities]. */
      get quantities() {
        return [1].concat(this.bundle ? this.bundle.variants.map((v) => v.quantity) : []);
      },

      get bundleVariant() {
        if (!this.bundle || this.quantity <= 1) return null;
        return this.bundle.variants.find((v) => v.quantity === this.quantity) || null;
      },

      get isBundle() {
        return !!this.bundleVariant;
      },

      get mainVariant() {
        return this.variants.find((v) => v.id === this.slots[0]) || null;
      },

      get gifts() {
        return this.bundleVariant ? this.bundleVariant.gifts || [] : [];
      },

      get properties() {
        return this.isBundle ? buildBundleProperties(this.slots, this.gifts) : null;
      },

      // ─── Actions ────────────────────────────────────────────────────────

      /** Select a pack size. Slots grow by copying slot 0, shrink by truncation. */
      setQuantity(quantity) {
        const q = Number(quantity) || 1;
        if (q === this.quantity) return;
        if (q > 1 && !(this.bundle && this.bundle.variants.some((v) => v.quantity === q))) {
          log.warn('setQuantity: no bundle variant for pack size', { quantity: q });
          return;
        }
        const next = this.slots.slice(0, q);
        while (next.length < q) next.push(this.slots[0]);
        this.slots = next;
        log.info('quantity', { quantity: q, slots: next });
        this.sync();
      },

      /**
       * Set the flavor in a slot. Slot 0 is the page's flavor (URL, ingredients,
       * Appstle) and goes through setFlavor; any other slot only changes the
       * add payload, so it updates the selection WITHOUT announcing a new
       * priced selection (the header has nothing new to show).
       */
      setSlot(index, variantId) {
        const id = Number(variantId);
        if (!this.variants.some((v) => v.id === id)) {
          log.warn('setSlot: unknown variant', { index, variantId });
          return;
        }
        if (index === 0) { this.setFlavor(id); return; }
        if (index < 0 || index >= this.slots.length || this.slots[index] === id) return;
        this.slots[index] = id;
        log.info('slot', { index, variantId: id });
        this.sync({ announce: false });
      },

      /** Change the page's flavor (slot 0): URL param + store:variant-changed for SSR panels. */
      setFlavor(variantId) {
        const id = Number(variantId);
        const variant = this.variants.find((v) => v.id === id);
        if (!variant || this.slots[0] === id) return;
        this.slots[0] = id;

        try {
          const url = new URL(location.href);
          url.searchParams.set('variant', String(id));
          history.replaceState({}, '', url.toString());
        } catch (_) {}

        log.info('flavor', { variantId: id });
        window.Theme.event('store:variant-changed', { productId: this.productId, variant });
        this.sync();
      },

      /**
       * Project state into $store.selection (the ATC source of truth) and —
       * unless `announce` is false — emit `store:card-selected` so the header
       * and per-day pill follow the newly priced selection. (The gallery is static
       * and listens to nothing — no per-taste or pack-shot swap.)
       * Always resets the selling plan; appstle-selection-bridge re-applies the
       * widget's plan for 1x on the emitted event.
       */
      sync({ announce = true } = {}) {
        const sel = Alpine.store('selection');
        if (!sel) return;
        const bv = this.bundleVariant;
        const priced = bv || this.mainVariant;
        if (!priced) return;
        const productId = bv ? this.bundle.productId : this.productId;

        sel.set({
          productId,
          variantId: priced.id,
          quantity: 1,
          price: priced.price,
          compareAtPrice: priced.compareAtPrice,
          available: priced.available,
          sellingPlanId: null,
          properties: this.properties,
        });

        if (!announce) return;

        const packQty = bv ? this.quantity : 1;
        const totalDays = packQty * this.servingsPerPack;
        const compare = priced.compareAtPrice;
        const { perServingCents, savePct } = recalcDerived(priced.price, compare, totalDays);

        window.Theme.event('store:card-selected', {
          productId,
          variantId: priced.id,
          isBundle: !!bv,
          packQty,
          totalDays,
          perServing: fmt(perServingCents),
          priceFormatted: fmt(priced.price),
          compareFormatted: compare ? fmt(compare) : '',
          savePct,
        });
      },

      init() {
        log.info('init', { productId: this.productId, bundle: !!this.bundle, slots: this.slots });
      },
    });

    // ─── flavorSlot(index) — one custom-select row of blocks/flavor-slots.liquid ──
    //
    // Per-instance UI state (open/active) for a listbox-pattern dropdown; the
    // selected value lives in $store.bundle.slots[index]. Registered here (not
    // inline) because up to 4 identical instances render per page.
    Alpine.data('flavorSlot', (index) => ({
      index,
      open: false,
      active: -1,

      get store() { return Alpine.store('bundle'); },
      get selectedId() { return this.store.slots[this.index]; },
      get selected() { return this.store.variants.find((v) => v.id === this.selectedId) || null; },

      // Via $refs (resolved against the component root), NOT this.$el: inside an
      // event handler Alpine binds $el to the element being evaluated (the <li>).
      options() { return Array.from((this.$refs.list || this.$el).querySelectorAll('[role="option"]')); },

      toggle() { if (this.open) this.close(true); else this.openList(); },

      openList() {
        this.open = true;
        const i = this.options().findIndex((o) => Number(o.dataset.variantId) === this.selectedId);
        this.active = i >= 0 ? i : 0;
        this.$nextTick(() => this.focusActive());
      },

      close(refocus = false) {
        if (!this.open) return;
        this.open = false;
        this.active = -1;
        if (refocus) this.$nextTick(() => { try { this.$refs.trigger.focus(); } catch (_) {} });
      },

      focusActive() {
        const o = this.options()[this.active];
        if (o) o.focus();
      },

      move(delta) {
        const n = this.options().length;
        if (!n) return;
        this.active = (this.active + delta + n) % n;
        this.focusActive();
      },

      choose(variantId) {
        const variant = this.store.variants.find((v) => v.id === Number(variantId));
        if (!variant || !variant.available) return;
        this.store.setSlot(this.index, variant.id);
        this.close(true);
      },

      onListKeydown(e) {
        switch (e.key) {
          case 'ArrowDown': e.preventDefault(); this.move(1); break;
          case 'ArrowUp': e.preventDefault(); this.move(-1); break;
          case 'Home': e.preventDefault(); this.active = 0; this.focusActive(); break;
          case 'End': e.preventDefault(); this.active = this.options().length - 1; this.focusActive(); break;
          case 'Enter':
          case ' ': {
            e.preventDefault();
            const o = this.options()[this.active];
            if (o) this.choose(o.dataset.variantId);
            break;
          }
          case 'Escape': e.preventDefault(); this.close(true); break;
          case 'Tab': this.close(false); break;
          default: break;
        }
      },
    }));
  });
})();
